/**
 * The camera bubble's place in the frame — the pure part.
 *
 * Everything is a fraction of the frame, so the same bubble sits in the
 * same place in the preview, in the recording, and after the window is
 * resized: `size` is the diameter as a fraction of the frame's height,
 * `x` and `y` the centre as fractions of the width and height. The frame's
 * pixel size is needed only to keep the circle inside it — a fraction of
 * the width is not a fraction of the height.
 */

export interface BubbleSpec {
  visible: boolean
  /** Diameter, as a fraction of the frame's height. */
  size: number
  /** Centre, as fractions of the frame's width and height. */
  x: number
  y: number
}

export type BubbleCorner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'

export interface Circle {
  cx: number
  cy: number
  r: number
}

/** Small, medium, large — Loom's three. */
export const BUBBLE_SIZES: readonly number[] = [0.2, 0.28, 0.38]

/** Bottom left, medium: where a presenter's face goes by habit. */
export const DEFAULT_BUBBLE: BubbleSpec = { visible: true, size: 0.28, x: 0.11, y: 0.8 }

/** Clear of the edge by this fraction of the frame's height. */
const MARGIN = 0.02

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v))

/** Where the circle is, in pixels, in a frame of `w` × `h`. */
export function bubbleCircle(spec: BubbleSpec, w: number, h: number): Circle {
  return { cx: spec.x * w, cy: spec.y * h, r: (spec.size * h) / 2 }
}

/** The same bubble, moved the least distance that puts it wholly inside the frame. */
export function clampBubble(spec: BubbleSpec, w: number, h: number): BubbleSpec {
  if (w <= 0 || h <= 0) return spec
  const r = (spec.size * h) / 2
  const m = MARGIN * h
  const minX = (r + m) / w
  const maxX = 1 - (r + m) / w
  const minY = (r + m) / h
  const maxY = 1 - (r + m) / h
  return {
    ...spec,
    x: minX <= maxX ? clamp(spec.x, minX, maxX) : 0.5,
    y: minY <= maxY ? clamp(spec.y, minY, maxY) : 0.5,
  }
}

/** Snap to a corner, clear of the edges. */
export function bubbleAtCorner(
  spec: BubbleSpec,
  corner: BubbleCorner,
  w: number,
  h: number,
): BubbleSpec {
  const x = corner.endsWith('left') ? 0 : 1
  const y = corner.startsWith('top') ? 0 : 1
  return clampBubble({ ...spec, x, y }, w, h)
}

/** Which corner the bubble is nearest, for the controls to show. */
export function cornerOf(spec: BubbleSpec): BubbleCorner {
  const v = spec.y < 0.5 ? 'top' : 'bottom'
  const hz = spec.x < 0.5 ? 'left' : 'right'
  return `${v}-${hz}`
}

/** Dragged to (x, y), fractions of the frame; kept inside it. */
export function moveBubbleTo(
  spec: BubbleSpec,
  x: number,
  y: number,
  w: number,
  h: number,
): BubbleSpec {
  return clampBubble({ ...spec, x, y }, w, h)
}

/** Is the point (fractions of the frame) on the bubble? */
export function hitsBubble(spec: BubbleSpec, x: number, y: number, w: number, h: number): boolean {
  if (!spec.visible || w <= 0 || h <= 0) return false
  const { cx, cy, r } = bubbleCircle(spec, w, h)
  const dx = x * w - cx
  const dy = y * h - cy
  return dx * dx + dy * dy <= r * r
}

/** The next size up or down, kept inside the frame. */
export function resizeBubble(spec: BubbleSpec, dir: 1 | -1, w: number, h: number): BubbleSpec {
  const current = BUBBLE_SIZES.reduce(
    (best, s, i) =>
      Math.abs(s - spec.size) < Math.abs((BUBBLE_SIZES[best] ?? 0) - spec.size) ? i : best,
    0,
  )
  const next = clamp(current + dir, 0, BUBBLE_SIZES.length - 1)
  return clampBubble({ ...spec, size: BUBBLE_SIZES[next] ?? spec.size }, w, h)
}
